"use client";

import { ArrowLeft, Check, ExternalLink, Clock, Copy, Info, X, Download, Palette, ShoppingCart, BookOpen, Globe, ChevronRight, Sparkles, type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { useState } from "react";
import { useToastContext } from "@/components/ui/toast-context";
import { Logo } from "@/components/ui/logo";
import { cn, reorderServicesForProgress } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookCoverImage } from "@/components/ui/book-cover-image";
import { PRECO_SERVICO, SERVICO_IDS } from "@/config/servicos";

import type { Livro } from '@/types/livro'

type ServiceKey = 'CAPA' | 'ISBN' | 'AMAZON' | 'TRADUCAO'

interface BookDetailPageProps {
  livro: Livro
  onVoltar?: () => void
}

export function BookDetailPage({ livro, onVoltar }: BookDetailPageProps) {

  const { orderedServices, percent, statusByKey } = reorderServicesForProgress(livro)
  const [selectedService, setSelectedService] = useState<string | null>(null)
  const { toast } = useToastContext()


  // Service-specific gradients and styling
  const serviceGradients: Record<ServiceKey, { bg: string; border: string; iconBg: string }> = {
    CAPA: {
      bg: "from-purple-500/10 via-pink-500/10 to-rose-500/10",
      border: "border-purple-200 dark:border-purple-800",
      iconBg: "bg-gradient-to-br from-purple-500 to-pink-500"
    },
    AMAZON: {
      bg: "from-orange-500/10 via-amber-500/10 to-yellow-500/10",
      border: "border-orange-200 dark:border-orange-800",
      iconBg: "bg-gradient-to-br from-orange-500 to-amber-500"
    },
    ISBN: {
      bg: "from-blue-500/10 via-cyan-500/10 to-teal-500/10",
      border: "border-blue-200 dark:border-blue-800",
      iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500"
    },
    TRADUCAO: {
      bg: "from-emerald-500/10 via-green-500/10 to-lime-500/10",
      border: "border-emerald-200 dark:border-emerald-800",
      iconBg: "bg-gradient-to-br from-emerald-500 to-green-500"
    }
  }

  // Service-specific icons
  const serviceIcons: Record<ServiceKey, LucideIcon> = {
    CAPA: Palette,
    AMAZON: ShoppingCart,
    ISBN: BookOpen,
    TRADUCAO: Globe
  }

  // External checkout URLs per service
  const serviceCheckoutUrls: Record<ServiceKey, string> = {
    CAPA: 'https://bot.responda-ai.com/capa-hub',
    AMAZON: 'https://bot.responda-ai.com/publicacao-amazon',
    ISBN: 'https://bot.responda-ai.com/hub-isbn',
    TRADUCAO: 'https://bot.responda-ai.com/traducao-hub',
  }



  if (livro.status_entrega && !statusByKey.livro) {
    console.warn('[BookDetailPage] Delivered book with statusByKey.livro=false', {
      id_pedido: livro.id_pedido,
      status_entrega: livro.status_entrega,
      statusByKey
    })
  }

  const servicesMeta = ([
    { key: 'CAPA' as const, label: 'Capa profissional', price: PRECO_SERVICO.CAPA, purchased: !!statusByKey.capa },
    { key: 'AMAZON' as const, label: 'Publicação Amazon', price: PRECO_SERVICO.AMAZON, purchased: !!statusByKey.amazon },
    { key: 'ISBN' as const, label: 'Registro ISBN', price: PRECO_SERVICO.ISBN, purchased: !!statusByKey.isbn },
    { key: 'TRADUCAO' as const, label: 'Tradução', price: PRECO_SERVICO.TRADUCAO, purchased: !!statusByKey.traducao },
  ]) as Array<{ key: 'CAPA'|'ISBN'|'AMAZON'|'TRADUCAO'; label: string; price: number; purchased: boolean }>

  const keyMap = { capa: 'CAPA', amazon: 'AMAZON', isbn: 'ISBN', traducao: 'TRADUCAO' } as const
  interface OrderedService {
    key: keyof typeof keyMap | string;
    label: string;
    done: boolean;
  }

  const nextProgressKey: keyof typeof keyMap | undefined = (orderedServices as OrderedService[])
    .find((s: OrderedService) => !s.done && s.key !== 'livro')
    ?.key as keyof typeof keyMap | undefined;
  const nextRecommended = nextProgressKey ? keyMap[nextProgressKey] : undefined

  type ServiceDetail = {
    title: string
    description: string
    benefits: string[]
    originalPrice?: number
    installments?: string
  }


  const serviceDetails: Record<ServiceKey, ServiceDetail> = {
    CAPA: {
      title: 'Capa Profissional',
      description: 'Design de capa feito por especialistas em livros best-sellers, a apenas 10% do valor de mercado e em até 7 dias úteis.',
      benefits: ['Design 100% único e profissional', 'Arquivo otimizado para impressão e e-book', 'Revisão inclusa', 'Entrega expressa garantida em até 7 dias'],
      originalPrice: 1200,
      installments: '12x de R$10,27'
    },
    ISBN: {
      title: 'Registro ISBN',
      description: 'Garanta a segurança e o reconhecimento profissional do seu livro, registrando-o como uma obra oficial.',
      benefits: ['Profissionalismo: Criamos sua conta oficial na Câmara Brasileira do Livro (CBL)', 'Segurança: Registramos o título da sua obra nacional e internacionalmente, garantindo sua titularidade', 'Exclusividade: Você se torna o único dono do seu título para sempre, com toda a segurança legal', 'Credibilidade: Seu livro recebe um registro que o profissionaliza e o torna rastreável em sistemas de livrarias e bibliotecas']
    },
    AMAZON: {
      title: 'Publicação na Amazon',
      description: 'Tenha seu livro na maior vitrine de vendas do mundo!',
      benefits: ['Publicação Profissional: Exponha seu livro na Amazon Brasil e globalmente', 'Controle Total dos Lucros: Receba 100% do seu dinheiro direto na sua conta, sem intermediários', 'Posicionamento Inteligente: Usamos técnicas de SEO avançadas para seu livro ser encontrado pelos leitores certos', 'Segurança e Suporte: Nosso time cuida de toda a parte técnica, deixando você focado apenas nas suas vendas']
    },
    TRADUCAO: {
      title: 'Tradução',
      description: 'Leve sua obra a milhões de novos leitores e ganhe em dólar e euro, sem perder a sua essência.',
      benefits: ['Qualidade: Tradução feita por profissionais para garantir fidelidade e fluidez', 'Segurança: Revisão inclusa para um resultado impecável e sem erros', 'Alcance: Seu livro traduzido para múltiplos idiomas, rompendo fronteiras', 'Autenticidade: A preservação total do seu estilo e voz originais']
    }
  }


  const formatPrice = (price: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price)

  const generateGoogleDocsEditUrl = (input: string) => {
    const trimmed = input.trim()
    if (/^https?:\/\//i.test(trimmed)) {
      const docMatch = trimmed.match(/https?:\/\/docs\.google\.com\/document\/d\/([a-zA-Z0-9-_]+)/)
      if (docMatch) return `https://docs.google.com/document/d/${docMatch[1]}/edit`
      const idParam = trimmed.match(/[?&](?:id|docID|docId)=([a-zA-Z0-9-_]+)/i)
      if (idParam) return `https://docs.google.com/document/d/${idParam[1]}/edit`
      const fileMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9-_]+)/)
      if (fileMatch) return `https://docs.google.com/document/d/${fileMatch[1]}/edit`
      const dMatch = trimmed.match(/\/d\/([a-zA-Z0-9-_]+)/)
      if (dMatch) return `https://docs.google.com/document/d/${dMatch[1]}/edit`
      return trimmed
    }
    return `https://docs.google.com/document/d/${encodeURIComponent(trimmed)}/edit`
  }

  const handleEditarLivro = () => {
    if (livro.link_editavel && livro.link_editavel.trim() !== '') {
      const url = generateGoogleDocsEditUrl(livro.link_editavel)
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      toast("Erro", "Link de edição não disponível para este livro.", "error");
    }
  }

  const handleDownloadPDF = () => {
    if (livro.link_pdf) {
      window.open(livro.link_pdf, '_blank', 'noopener,noreferrer')
    }
  }

  const [copiedId, setCopiedId] = useState(false)
  const copyBookId = async () => {
    // 1. Feature detection for Clipboard API
    const isClipboardApiAvailable = navigator.clipboard && window.isSecureContext;

    try {
      if (isClipboardApiAvailable) {
        // 2. Use Clipboard API
        await navigator.clipboard.writeText(String(livro.id_pedido));
        toast('Copiado', 'ID do livro copiado para a área de transferência', 'success');
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 1600);
      } else {
        // 3. Fallback for older browsers or insecure contexts
        window.prompt('Copiar ID:', String(livro.id_pedido));
      }

      // Haptic feedback (optional, suppress exceptions)
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nav: any = navigator;
        if (typeof nav.vibrate === 'function') {
          nav.vibrate(10);
        }
      } catch {
        // Suppress vibration errors
      }
    } catch (err) {
      // 4. Error handling for clipboard write
      console.error('Failed to copy ID:', err);
      toast('Erro', 'Não foi possível copiar o ID', 'error');
      // Fallback in case of clipboard API failure
      window.prompt('Copiar ID:', String(livro.id_pedido));
    }
  };

  const handleComprar = (servico: 'CAPA' | 'ISBN' | 'AMAZON' | 'TRADUCAO') => {
    try {
      const baseUrl = serviceCheckoutUrls[servico as ServiceKey]
      const url = `${baseUrl}?pedido=${encodeURIComponent(livro.id_pedido)}`
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      console.error('Erro ao abrir checkout do serviço', servico, e)
      toast("Erro de Checkout", "Não foi possível abrir o checkout deste serviço.", "error");
    }
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Header */}
      <header className="relative z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onVoltar}
            aria-label="Voltar ao dashboard"
            className="gap-2 h-10 sm:h-9"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <div className="h-8 xs:h-9 sm:h-10">
            <Logo className="h-8 xs:h-9 sm:h-10 mx-0" />
          </div>

          <span className="text-muted-foreground">/</span>
          <span className="font-medium">Detalhes do Livro</span>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Conteúdo principal (2 colunas) */}
          <div className="md:col-span-2 lg:col-span-2 space-y-6">
            {/* Card: Info do Livro */}
            <div className="bg-card rounded-xl shadow-sm border p-6">
              <div className="flex flex-col xs:flex-row gap-4 sm:gap-6">
                {/* Capa */}
                <div className="flex-shrink-0">
                  <div className="w-28 xs:w-32 sm:w-36">
                    <BookCoverImage
                      capa={livro.capa}
                      titulo={livro.titulo}
                      variant="cover"
                      size="md"
                      showDownloadButton={!!livro.capa?.imagem_capa || !!livro.capa?.imagem_capa_png}
                    />
                  </div>
                </div>

                {/* Detalhes */}
                <div className="flex-1">
                  <div className="mb-3">
                    <h1 className="mb-1">{livro.titulo}</h1>
                    {livro.nome_autor && (
                      <p className="text-muted-foreground mb-3">por {livro.nome_autor}</p>
                    )}
                    {livro.subtitulo && (
                      <p className="text-muted-foreground">{livro.subtitulo}</p>
                    )}
                  </div>

                  <div className="flex gap-3 sm:gap-2">
                    <Button onClick={handleEditarLivro} disabled={!livro.link_editavel} className="gap-2 h-12 sm:h-10">
                      Editar meu livro <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" onClick={handleDownloadPDF} disabled={!livro.link_pdf} className="h-12 sm:h-10 gap-2">
                      Download PDF <Download className="h-4 w-4" />
                    </Button>
                  </div>
                  {!livro.link_editavel && (
                    <p className="text-xs text-muted-foreground mt-2">Link de edição não disponível para este livro</p>
                  )}
                </div>
              </div>
            </div>

            {/* Card: Detalhes + Funil */}
            <div className="bg-card rounded-xl shadow-sm border p-6">
              <h2 className="mb-4">Detalhes</h2>

              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-6 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground">Idioma</p>
                  <p className="font-medium">{livro.idioma}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <span className={cn("inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                    statusByKey.livro ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                  )}>{statusByKey.livro ? 'Entregue' : 'Em produção'}</span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">ID do Livro</p>
                  <div className="relative flex items-center gap-2">
                    <p className="font-medium">{livro.id_pedido}</p>
                    <Button variant="ghost" size="sm" onClick={copyBookId} className="h-10 w-10 xs:h-8 xs:w-8 p-0" aria-label="Copiar ID do livro">
                      <Copy className="h-3 w-3" />
                    </Button>
                    {copiedId && (
                      <span role="status" aria-live="polite" className="absolute -top-6 right-0 text-[11px] font-medium rounded-md bg-emerald-600 text-white px-2 py-0.5 shadow-md">
                        Copiado!
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3>Funil de Publicação</h3>
                  <span className="text-xs sm:text-sm font-medium text-muted-foreground">{percent}%</span>
                </div>
                <div className="mb-4"><Progress value={percent} className="h-2" /></div>

                <div className="flex justify-between items-center">
                  {(() => {
                    const steps = orderedServices
                    interface Step {
                      key: string;
                      label: string;
                      done: boolean;
                    }

                    return steps.map((step: Step) => (
                      <div key={step.key} className="flex flex-col items-center">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center mb-2",
                          step.done ? "bg-blue-600 text-white" : "bg-muted text-muted-foreground"
                        )}>
                          {step.done ? <Check className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                        </div>
                        <span className="text-xs text-center text-muted-foreground max-w-[4rem] break-words">{step.label}</span>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar (1 coluna) */}
          <div className="md:col-span-2 lg:col-span-1 space-y-6">
            {/* Serviços adicionais */}
            <div className="bg-card rounded-xl shadow-sm border p-6">
              <h3 className="mb-4 font-semibold text-lg">Serviços adicionais</h3>
              <div className="space-y-4">
                {servicesMeta.map((s) => {
                  const canBuy = Boolean(SERVICO_IDS[s.key]);
                  const isNextRecommended = s.key === nextRecommended && canBuy && !s.purchased;
                  const gradient = serviceGradients[s.key];
                  const IconComponent = serviceIcons[s.key];

                  return (
                    <motion.div
                      key={s.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedService(s.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedService(s.key);
                        }
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={cn(
                        "group relative flex items-center gap-2 sm:gap-4 p-3 sm:p-4 md:p-5 rounded-lg border-2 cursor-pointer transition-all duration-300 ease-out shadow-sm hover:shadow-lg backdrop-blur-sm",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
                        `bg-gradient-to-r ${gradient.bg} ${gradient.border}`,
                        s.purchased && "bg-success/5",
                        isNextRecommended && "ring-2 ring-primary/60"
                      )}
                    >
                      {/* Icon Container */}
                      <div className={cn(
                        "flex items-center justify-center rounded-lg p-2 sm:p-3 text-white shadow-md shrink-0",
                        gradient.iconBg,
                        isNextRecommended && "animate-pulse"
                      )}>
                        <IconComponent className="h-5 w-5 sm:h-6 sm:w-6" />
                      </div>

                      {/* Service Label */}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm sm:text-base font-medium">{s.label}</span>
                      </div>

                      {/* Badges and Arrow */}
                      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        {s.purchased && (
                          <Badge
                            variant="outline"
                            className="bg-success/10 text-success border-success/20 text-xs whitespace-nowrap"
                          >
                            <Check className="h-3 w-3 mr-1" aria-hidden="true" />
                            <span className="hidden xs:inline">Adquirido</span>
                            <span className="xs:hidden">OK</span>
                          </Badge>
                        )}
                        {isNextRecommended && !s.purchased && (
                          <Badge
                            variant="default"
                            className="bg-primary/90 text-xs whitespace-nowrap"
                          >
                            <Sparkles className="h-3 w-3 mr-1 hidden xs:inline" aria-hidden="true" />
                            <span className="hidden sm:inline">Recomendado</span>
                            <span className="sm:hidden">Rec.</span>
                          </Badge>
                        )}
                        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground group-hover:text-foreground transition-colors" aria-hidden="true" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
              <DialogContent className="max-w-2xl p-0 !overflow-visible !max-h-none sm:!max-h-none">
                {selectedService && serviceDetails[selectedService as keyof typeof serviceDetails] && (() => {
                  const gradient = serviceGradients[selectedService as ServiceKey];
                  const IconComponent = serviceIcons[selectedService as ServiceKey];
                  const current = servicesMeta.find(s => s.key === selectedService);
                  const canBuy = current ? Boolean(SERVICO_IDS[current.key]) : false;

                  return (
                    <>
                      {/* Header + content in a clean card layout inspired by the provided mockups */}
                      <div className="relative w-full p-5 sm:p-6 space-y-5">
                        {/* Title row with close aligned */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className={cn(
                              "flex items-center justify-center rounded-full p-3 text-white shadow-md",
                              gradient.iconBg
                            )}>
                              <IconComponent className="h-6 w-6" />
                            </div>
                            <DialogTitle className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
                              {serviceDetails[selectedService as keyof typeof serviceDetails].title}
                            </DialogTitle>
                          </div>
                          <DialogClose asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full hover:bg-muted mt-0.5"
                              aria-label="Fechar"
                            >
                              <X className="h-5 w-5" />
                            </Button>
                          </DialogClose>
                        </div>

                        {/* Info box */}
                        <div className={cn(
                          "rounded-lg p-4 border shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)] flex items-start gap-3",
                          `bg-gradient-to-r ${gradient.bg} ${gradient.border}`
                        )}>
                          <Info className="h-5 w-5 mt-0.5 flex-shrink-0 text-foreground/70" />
                          <p className="text-sm sm:text-base text-foreground/90">
                            {serviceDetails[selectedService as keyof typeof serviceDetails].description}
                          </p>
                        </div>

                        {/* Benefits card */}
                        <div className="bg-card rounded-lg shadow-md dark:shadow-[0_10px_20px_-5px_rgba(0,0,0,0.55)] p-6 border border-border/50 dark:border-white/10">
                          <h4 className="font-display text-base sm:text-lg font-semibold mb-4">Benefícios incluídos:</h4>
                          <ul className="space-y-3">
                            {serviceDetails[selectedService as keyof typeof serviceDetails].benefits.map((benefit, index) => (
                              <li key={index} className="flex items-center gap-3 text-sm sm:text-base leading-relaxed">
                                <div className={cn("flex items-center justify-center rounded-full p-1.5 text-white", gradient.iconBg)}>
                                  <Check className="h-4 w-4" />
                                </div>
                                <span className="text-foreground">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Price + CTA */}
                        <div className="bg-card rounded-lg shadow-md dark:shadow-[0_10px_20px_-5px_rgba(0,0,0,0.55)] p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-border/50 dark:border-white/10">
                          <div className="w-full text-center sm:text-left">
                            <span className="text-sm text-muted-foreground">Investimento</span>
                            {serviceDetails[selectedService as keyof typeof serviceDetails].originalPrice && (
                              <div className="text-sm text-muted-foreground line-through">
                                {formatPrice(serviceDetails[selectedService as keyof typeof serviceDetails].originalPrice!)}
                              </div>
                            )}
                            <div className="text-3xl sm:text-4xl font-display font-bold tracking-tight">
                              {formatPrice(current?.price || 0)}
                            </div>
                            {serviceDetails[selectedService as keyof typeof serviceDetails].installments && (
                              <div className="text-sm text-muted-foreground">
                                ou {serviceDetails[selectedService as keyof typeof serviceDetails].installments}
                              </div>
                            )}
                          </div>

                          {current?.purchased ? (
                            <Badge variant="success" className="shrink-0 h-10 px-4 text-sm mx-auto sm:mx-0">
                              <Check className="h-4 w-4 mr-2" />
                              Já adquirido
                            </Badge>
                          ) : (
                            <Button
                              className={cn(
                                "h-12 sm:h-11 shrink-0 px-6 font-semibold shadow-lg hover:shadow-xl transition-all w-full sm:w-auto max-w-[260px] mx-auto sm:mx-0",
                                gradient.iconBg
                              )}
                              onClick={() => current && handleComprar(current.key)}
                              disabled={!canBuy}
                              title={!canBuy ? 'Indisponível no momento' : undefined}
                            >
                              <ShoppingCart className="h-4 w-4 mr-2" />
                              Comprar agora
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </DialogContent>
            </Dialog>

          </div>
        </div>
      </main>
    </div>
  )
}

